import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Button, DatePicker, Input } from 'antd-v5';
import { getVendorById, getVendorItemCollections, getVendorItemFamilies, getVendorItemMaterials } from '../../../../api/vendors';
import { getAllMerchantTags } from '../../../../services/clients';
import { getRequest } from '../../../../core/apiRequests';
import useVendorByKey from '../../../../hooks/query/useVendorByKey';
import ReportFilter from '../../../../componentsV2/CustomizedAntDesign/ReportFilter';
import ReportTagsFilters from '../../../../componentsV2/CustomizedAntDesign/ReportTagsFilters';

const { Search } = Input;
const { RangePicker } = DatePicker;

const InquiryFilters = ({ filters, setFilters, onReset, auth }) => {
  const { vendorId: rolexVendorId } = useVendorByKey('rolex');
  const [vendorDetails, setVendorDetails] = useState(null);
  const [associateOptions, setAssociateOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [collectionsOptions, setCollectionsOptions] = useState([]);
  const [familiesOptions, setFamiliesOptions] = useState([]);
  const [materialsOptions, setMaterialsOptions] = useState([]);

  // Fetch vendor details to know which filters are available
  useEffect(() => {
    if (!rolexVendorId) return;
    getVendorById(rolexVendorId)
      .then(response => setVendorDetails(response?.data || response))
      .catch(() => setVendorDetails(null));
  }, [rolexVendorId]);

  // Fetch associates
  useEffect(() => {
    if (auth?.merchantId) {
      getRequest({ params: ['associates', auth.merchantId], query: { singleStore: 0 }, loadingStatusOff: true }).then(data => {
        if (Array.isArray(data)) {
          const sorted = data
            .map(a => ({
              label: a.fullName || `${a.first_name || ''} ${a.last_name || ''}`.trim(),
              value: a.id?.toString(),
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
          setAssociateOptions(sorted);
        }
      });
    }
  }, [auth?.merchantId]);

  // Fetch tags
  useEffect(() => {
    getAllMerchantTags({ loadingStatusOff: true })
      .then(merchantTags => {
        const sorted = (merchantTags || [])
          .map(tag => ({ label: tag.name, value: tag.name }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setTagOptions(sorted);
      })
      .catch(() => setTagOptions([]));
  }, []);

  // Fetch vendor item filters (collections, families, materials)
  useEffect(() => {
    if (!rolexVendorId) return;

    if (vendorDetails?.hasCollection) {
      getVendorItemCollections([rolexVendorId])
        .then(response => {
          setCollectionsOptions(
            (response?.data || [])
              .map(item => ({ label: item.name, value: item.name }))
              .sort((a, b) => a.label.localeCompare(b.label)),
          );
        })
        .catch(() => setCollectionsOptions([]));
    }

    if (vendorDetails?.hasFamily) {
      getVendorItemFamilies([rolexVendorId])
        .then(response => {
          setFamiliesOptions(
            (response?.data || [])
              .map(item => ({ label: item.name, value: item.name }))
              .sort((a, b) => a.label.localeCompare(b.label)),
          );
        })
        .catch(() => setFamiliesOptions([]));
    }

    if (vendorDetails?.hasMaterial) {
      getVendorItemMaterials([rolexVendorId])
        .then(response => {
          setMaterialsOptions(
            (response?.data || [])
              .map(item => ({ label: item.name, value: item.name }))
              .sort((a, b) => a.label.localeCompare(b.label)),
          );
        })
        .catch(() => setMaterialsOptions([]));
    }
  }, [rolexVendorId, vendorDetails]);

  // Build filter options for the ReportFilter component
  const filterOptions = useMemo(() => {
    const options = [];
    let keyIndex = 0;

    // Status filter
    options.push({
      title: 'Status',
      key: `0-${keyIndex++}`,
      value: 'status',
      children: [
        { key: `0-${keyIndex - 1}-0`, title: 'Waiting', value: 'WAITING' },
        { key: `0-${keyIndex - 1}-1`, title: 'Ordered', value: 'ORDERED' },
        { key: `0-${keyIndex - 1}-2`, title: 'Canceled', value: 'CANCELED' },
        { key: `0-${keyIndex - 1}-3`, title: 'Fulfilled', value: 'FULFILLED' },
      ],
    });

    // Associate filter
    if (associateOptions.length > 0) {
      options.push({
        title: 'Associate',
        key: `0-${keyIndex++}`,
        value: 'associate',
        children: associateOptions.map((opt, i) => ({
          key: `0-${keyIndex - 1}-${i}`,
          title: opt.label,
          value: opt.value,
        })),
      });
    }

    // Tag filter
    if (tagOptions.length > 0) {
      options.push({
        title: 'Tag',
        key: `0-${keyIndex++}`,
        value: 'tags',
        children: tagOptions.map((opt, i) => ({
          key: `0-${keyIndex - 1}-${i}`,
          title: opt.label,
          value: opt.value,
        })),
      });
    }

    // Collection filter
    if (collectionsOptions.length > 0) {
      options.push({
        title: 'Collection',
        key: `0-${keyIndex++}`,
        value: 'collections',
        children: collectionsOptions.map((opt, i) => ({
          key: `0-${keyIndex - 1}-${i}`,
          title: opt.label,
          value: opt.value,
        })),
      });
    }

    // Family filter
    if (familiesOptions.length > 0) {
      options.push({
        title: 'Family',
        key: `0-${keyIndex++}`,
        value: 'families',
        children: familiesOptions.map((opt, i) => ({
          key: `0-${keyIndex - 1}-${i}`,
          title: opt.label,
          value: opt.value,
        })),
      });
    }

    // Material filter
    if (materialsOptions.length > 0) {
      options.push({
        title: 'Material',
        key: `0-${keyIndex++}`,
        value: 'materials',
        children: materialsOptions.map((opt, i) => ({
          key: `0-${keyIndex - 1}-${i}`,
          title: opt.label,
          value: opt.value,
        })),
      });
    }

    return options;
  }, [associateOptions, tagOptions, collectionsOptions, familiesOptions, materialsOptions]);

  // Handle filter changes from ReportFilter component
  const handleFilterChange = useCallback(
    value => {
      const updatedFilters = {
        status: [],
        associate: [],
        tags: [],
        collections: [],
        families: [],
        materials: [],
      };

      value.forEach(item => {
        if (item[0] && item[1]) {
          updatedFilters[item[0]] = [...updatedFilters[item[0]], item[1]];
        } else {
          updatedFilters[item[0]] = [];
        }
      });

      setFilters(prev => ({
        ...prev,
        ...updatedFilters,
      }));
    },
    [setFilters],
  );

  // Remove individual filter
  const removeFilter = useCallback(
    (key, value) => {
      setFilters(prev => ({
        ...prev,
        [key]: prev[key].filter(filter => filter !== value),
      }));
    },
    [setFilters],
  );

  // Check if there are active filters (excluding clientSearch and dateRange)
  const hasActiveFilters = !!(
    filters.clientSearch ||
    filters.status?.length ||
    filters.associate?.length ||
    filters.tags?.length ||
    filters.collections?.length ||
    filters.families?.length ||
    filters.materials?.length ||
    filters.dateRange
  );

  // Build filters object for ReportFilter and ReportTagsFilters
  const filterState = useMemo(
    () => ({
      status: filters.status || [],
      associate: filters.associate || [],
      tags: filters.tags || [],
      collections: filters.collections || [],
      families: filters.families || [],
      materials: filters.materials || [],
    }),
    [filters],
  );

  return (
    <>
      <div className="rolex-inquiries__filters">
        <Search
          placeholder="Find a client"
          style={{ width: 185 }}
          value={filters.clientSearch}
          onChange={e => setFilters(prev => ({ ...prev, clientSearch: e.target.value }))}
          allowClear
          enterButton
          data-testid="inquiries__filters--client-search"
        />
        <span data-testid="inquiries__filters--date-range">
          <RangePicker value={filters.dateRange} onChange={dates => setFilters(prev => ({ ...prev, dateRange: dates }))} />
        </span>
        <ReportFilter
          options={filterOptions}
          handleFilterChange={handleFilterChange}
          filters={filterState}
          data-testid="inquiries__filters--filter-button"
        />
        {hasActiveFilters && (
          <Button type="link" onClick={onReset} style={{ padding: 0 }} data-testid="inquiries__filters--reset-button">
            Reset filters
          </Button>
        )}
      </div>
      <div style={{ marginBottom: '16px' }}>
        <ReportTagsFilters options={filterOptions} filterSelected={filterState} removeFilter={removeFilter} />
      </div>
    </>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(InquiryFilters);
